import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TableWithSearchFilterTemplate from '../../../ui/components/TableWithSearchFilterTemplate/TableWithSearchFilterTemplate';
import { useAPICall } from '../../../hooks';
import { DynamicObjectInterface } from '../../../sdk/type';
import ParticipantService from '../../../sdk/services/participantService';
import { DashboardProps } from '../dashboardConfig';
import { Card, HeaderStyle, useBreakpoint } from '../../../ui/components';
import { getParticipantListConfig } from './participantListConfig';
import styles from './list.module.scss';

export interface ParticipantData {
  name: string;
  loginId: string;
  weightIn?: { weightInDate?: string };
  weightOut?: { weightOutDate?: string };
  isIndividualAchieved?: number;
  subscribe?: { orderNumber?: number }[];
  userId: number;
  status?: string;
}

export const ParticipantList = (props: DashboardProps) => {
  const { index, campaignData } = props;

  const [participants, setParticipants] = useState<ParticipantData[]>([]);
  const [filteredData, setFilteredData] = useState<ParticipantData[]>([]);
  const [searchKey, setSearchKey] = useState('');

  const navigate = useNavigate();
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm'].indexOf(viewPortType) === -1;

  const initialFilterList = [
    {
      filterName: 'Pre-Assessment',
      filterOptions: [
        { label: 'Completed', checked: false },
        { label: 'Not Completed', checked: false },
      ],
      isOpen: false,
    },
    {
      filterName: 'Post-Assessment',
      filterOptions: [
        { label: 'Completed', checked: false },
        { label: 'Not Completed', checked: false },
      ],
      isOpen: false,
    },
    {
      filterName: 'Achievement Of Results',
      filterOptions: [
        { label: 'Achieved', checked: false },
        { label: 'Not Achieved', checked: false },
      ],
      isOpen: false,
    },
    // {
    //   filterName: 'Subscribed to ALP',
    //   filterOptions: [
    //     { label: 'Subscribed', checked: false },
    //     { label: 'Not Subscribed', checked: false },
    //   ],
    //   isOpen: false,
    // },
  ];

  const [filterList, setFilterList] = useState(initialFilterList);
  const [tempFilterList, setTempFilterList] = useState(initialFilterList);

  const apiCall = useAPICall();

  useEffect(() => {
    (async () => {
      const payload = {
        campaignId: campaignData[index]?.campaignId,
      };

      let apiList = [apiCall(ParticipantService.getParticipants, payload)];
      let res = await Promise.all(apiList);

      let participants = res?.[0]?.data?.data?.filter((i: ParticipantData) => i.status === 'ACTIVE') || [];

      setParticipants(participants);
      setFilteredData(participants);
    })();
  }, [campaignData[index]?.campaignId]);

  const handleSearch = (text: string) => {
    setSearchKey(text);

    const newFilteredData = participants.filter(participant => {
      return tempFilterList.every(filter => {
        const selectedOption = filter.filterOptions.find(option => option.checked);
        if (!selectedOption) {
          return true; // No filter option selected, include all
        }
        const filterName = filter.filterName;
        if (filterName === 'Pre-Assessment') {
          return selectedOption.label === 'Completed'
            ? participant?.weightIn?.weightInDate
            : !participant?.weightIn?.weightInDate;
        } else if (filterName === 'Post-Assessment') {
          return selectedOption.label === 'Completed'
            ? participant?.weightOut?.weightOutDate
            : !participant?.weightOut?.weightOutDate;
        } else if (filterName === 'Achievement Of Results') {
          return selectedOption.label === 'Achieved'
            ? participant?.isIndividualAchieved === 1
            : participant?.isIndividualAchieved !== 1;
        } else if (filterName === 'Subscribed to ALP') {
          return selectedOption.label === 'Subscribed'
            ? participant?.subscribe && participant?.subscribe?.length > 0
            : !(participant?.subscribe && participant?.subscribe?.length > 0);
        }
        return true; // Include all if filter name doesn't match
      });
    });

    // Then filter the already filtered data based on the search key
    const finalFilteredData = newFilteredData.filter(
      i => i?.name?.toLowerCase()?.includes(text?.toLowerCase()) || i?.loginId?.toString()?.includes(text),
    );

    setFilteredData(finalFilteredData);
  };

  const handleOnRowClick = (rowData: DynamicObjectInterface) => {
    navigate(`/campaigns/${campaignData[index]?.campaignId}/participants/${rowData?.teamId}/user?id=${rowData.userId}`);
  };

  const handleToggleFilterOpen = (filterIndex: number) => {
    setTempFilterList(prevList =>
      prevList.map((filter, index) => (index === filterIndex ? { ...filter, isOpen: !filter.isOpen } : filter)),
    );
  };

  const handleToggleOptionChecked = (filterIndex: number | undefined, optionIndex: number) => {
    setTempFilterList(prevList =>
      prevList.map((filter, index) => ({
        ...filter,
        filterOptions: filter.filterOptions.map((option, idx) => ({
          ...option,
          checked: index === filterIndex && idx === optionIndex ? !option.checked : false,
        })),
      })),
    );
  };

  const handleCancel = () => {
    // Reset the tempFilterList to match the filterList (the original state)
    setTempFilterList(
      filterList.map(filter => ({
        ...filter,
        isOpen: false, // Close all filters
      })),
    );
  };

  const handleApplyChanges = async () => {
    await handleSearch(searchKey);

    // Update the main filter list state
    setFilterList(tempFilterList);

    // Close all filter drawers
    setTempFilterList(prevList =>
      prevList.map(filter => ({
        ...filter,
        isOpen: false,
      })),
    );
  };

  const handleResetFilters = () => {
    setFilterList(initialFilterList);
    setTempFilterList(initialFilterList);
    setFilteredData(participants);
    setSearchKey('');
  };

  return (
    <div className={styles['dashboard-page']}>
      <div className={styles['dashboard-page-title']}>Participants</div>

      {isDesktopView ? (
        <Card title={`Campaign: ${campaignData[index]?.campaignName}`} headerStyle={HeaderStyle.HEADER_5}>
          {filteredData?.length > 0 && (
            <TableWithSearchFilterTemplate
              dataSource={filteredData}
              getColumns={getParticipantListConfig}
              rowKey="recId"
              handleSearch={handleSearch}
              handleReset={() => handleSearch('')}
              onRowClick={handleOnRowClick}
              searchKey={searchKey}
              tempFilterList={tempFilterList}
              filterList={filterList}
              onToggleFilterOpen={handleToggleFilterOpen}
              onToggleOptionChecked={handleToggleOptionChecked}
              handleApplyChanges={handleApplyChanges}
              onClose={handleCancel} // Pass handleCancel as onClose
              handleResetFilters={handleResetFilters}
            />
          )}
        </Card>
      ) : (
        <>
          <div className={styles['dashboard-page-subtitle']}>Campaign : {campaignData[index]?.campaignName}</div>
          {filteredData?.length > 0 && (
            <TableWithSearchFilterTemplate
              dataSource={filteredData}
              getColumns={getParticipantListConfig}
              rowKey="recId"
              handleSearch={handleSearch}
              handleReset={() => handleSearch('')}
              onRowClick={handleOnRowClick}
              searchKey={searchKey}
              tempFilterList={tempFilterList}
              filterList={filterList}
              onToggleFilterOpen={handleToggleFilterOpen}
              onToggleOptionChecked={handleToggleOptionChecked}
              handleApplyChanges={handleApplyChanges}
              onClose={handleCancel} // Pass handleCancel as onClose
              handleResetFilters={handleResetFilters}
            />
          )}
        </>
      )}
    </div>
  );
};
